import appConfig from '../../config/appConfig';
import deviceSettingTypes from '../../consts/device/deviceSettingTypes';
import getFeatureTypes from '../../enums/app/getFeatureTypes';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import versionUtils from '../../utils/versionUtils';
import connectivityService from '../device/connectivityService';
import iotDeviceSettingsService from './iotDeviceSettingsService';

let deviceGlobalFeaturesConfig;
let deviceManagementFeaturesConfig;
let upperBoundFeatures = new Set();

const FEATURE_TYPES = {
    AUTOSTART_FEATURE: 'autostart_feature',
    CONNECT_CARE_MODE: 'connect_care_mode',
    DEVICE_RESET: 'device_reset',
    DEVICE_ALARM: 'device_alarm',
    DEVICE_HOLDER_CHARGED_VIBRATION: 'device_holder_charged_vibration',
    DEVICE_LED_INTENSITY_MODE: 'device_led_intensity_mode',
    EXTENDED_DEVICE_LED_INTENSITY_MODE: 'extended_device_led_intensity_mode',
    DEVICE_PARENT_PROTECTION_MODE: 'device_parent_protection_mode',
    DEVICE_RESPONSIVE_DRAW_PROFILE_MODE: 'device_responsive_draw_profile_mode',
    DEVICE_SELF_CLEANING: 'device_self_cleaning',
    DEVICE_VAPE_CLOUD_SIZE_MODE: 'device_vape_cloud_size_mode',
    EXTENDED_HAPTIC_PROFILE_VIBRATION: 'extended_haptic_profile_vibration',
    HOLDER_LED_INTENSITY_MODE: 'holder_led_intensity_mode',
    EXTENDED_HOLDER_LED_INTENSITY_MODE: 'extended_holder_led_intensity_mode',
    SETTING_SUPPORTED: 'setting_supported',
    COMMON_VIBRATION_SWITCHER: 'common_vibration_switcher',
    START_CHARGING_VIBRATION: 'start_charging_vibration',
    STATUS_GESTURES: 'status_gestures',
    COUNTDOWN_MODE: 'countdown_mode',
    PROGRAMMABLE_VIBRATIONS: 'programmable_vibrations',
    SESSION_PROFILING: 'session_profiling',
    ADVANCED_DIAGNOSTIC: 'advanced_diagnostic',
    AEM_FLEX_BATTERY: 'aem_flex_battery',
    AEM_PAUSE_MODE: 'aem_pause_mode',
    FLEX_PUFF_FEATURE: 'flex_puff',
    IDLE_PING: 'idle_ping',
};

const resetGlobalConfig = () => {
    deviceGlobalFeaturesConfig = {
        is_holder_check: false,
        is_autostart: false,
        is_led_intensity: false,
        is_extended_led_intensity: false,
        is_vibration: false,
        is_self_cleaning: false,
        is_device_alarm: false,
        is_status_gestures: false,
        is_parent_protection: false,
        is_session_profiling: false,
        is_advanced_diagnostic: false,
        is_vape_cloud_size: false,
        is_responsive_draw: false,
        is_reset: false,
        is_connect_care: false,
        is_flex_puff: false,
        is_idle_ping: false,
    };
};

const resetManagementConfig = () => {
    deviceManagementFeaturesConfig = {
        [FEATURE_TYPES.AUTOSTART_FEATURE]: false,
        [FEATURE_TYPES.CONNECT_CARE_MODE]: false,
        [FEATURE_TYPES.DEVICE_RESET]: false,
        [FEATURE_TYPES.DEVICE_ALARM]: false,
        [FEATURE_TYPES.DEVICE_HOLDER_CHARGED_VIBRATION]: false,
        [FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: false,
        [FEATURE_TYPES.EXTENDED_DEVICE_LED_INTENSITY_MODE]: false,
        [FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: false,
        [FEATURE_TYPES.DEVICE_RESPONSIVE_DRAW_PROFILE_MODE]: false,
        [FEATURE_TYPES.DEVICE_SELF_CLEANING]: false,
        [FEATURE_TYPES.DEVICE_VAPE_CLOUD_SIZE_MODE]: false,
        [FEATURE_TYPES.EXTENDED_HAPTIC_PROFILE_VIBRATION]: false,
        [FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: false,
        [FEATURE_TYPES.EXTENDED_HOLDER_LED_INTENSITY_MODE]: false,
        programmable_vibrations: {
            [FEATURE_TYPES.SETTING_SUPPORTED]: false,
            [FEATURE_TYPES.COMMON_VIBRATION_SWITCHER]: false,
        },
        [FEATURE_TYPES.START_CHARGING_VIBRATION]: false,
        [FEATURE_TYPES.STATUS_GESTURES]: false,
        [FEATURE_TYPES.COUNTDOWN_MODE]: false,
        [FEATURE_TYPES.SESSION_PROFILING]: false,
        [FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: false,
        [FEATURE_TYPES.AEM_FLEX_BATTERY]: false,
        [FEATURE_TYPES.AEM_PAUSE_MODE]: false,
        [FEATURE_TYPES.FLEX_PUFF_FEATURE]: false,
        [FEATURE_TYPES.IDLE_PING]: false,
    };
};

const resetManagementFeaturesConfig = (iotDevice) => {
    resetManagementConfig();
    upperBoundFeatures = new Set();

    initManagementFeaturesConfig(iotDevice);
};

const allVersionSupported = (isHolderCheckNeeded) => ({
    version: '>= 0.0.0',
    value: true,
    isHolderCheck: isHolderCheckNeeded,
});

const DeviceCleaningType = {
    NOT_AVAILABLE: 0,
    FULL_AVAILABLE: 1,
    AVAILABLE_WHILE_CHARGING: 2,
};

const getIsCommonVibrationSwitcher = () =>
    deviceManagementFeaturesConfig?.programmable_vibrations?.[FEATURE_TYPES.SETTING_SUPPORTED];

const VERSIONS_ACCORDING = {
    [DEVICE_TYPES.DEV_TYPE_V24P_CHARGER]: [
        {
            [FEATURE_TYPES.DEVICE_SELF_CLEANING]: [
                {version: '>= 2.3.0', value: DeviceCleaningType.FULL_AVAILABLE, isHolderCheck: true},
            ],
        },
        {[FEATURE_TYPES.DEVICE_ALARM]: [{version: '>= 2.2.0', value: true, isHolderCheck: true}]},
        {[FEATURE_TYPES.DEVICE_HOLDER_CHARGED_VIBRATION]: [{version: '>= 2.2.0', value: true}]},
        {[FEATURE_TYPES.CONNECT_CARE_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
    ],
    [DEVICE_TYPES.DEV_TYPE_V24P_HOLDER]: [
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {
            [FEATURE_TYPES.COMMON_VIBRATION_SWITCHER]: [
                {
                    parentNode: 'programmable_vibrations',
                    version: '>= 0.0.0',
                    value: getIsCommonVibrationSwitcher,
                },
            ],
        },
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V30_CHARGER]: [
        {
            [FEATURE_TYPES.DEVICE_SELF_CLEANING]: [
                {
                    version: '>= 1.1.2',
                    value: DeviceCleaningType.FULL_AVAILABLE,
                    isHolderCheck: true,
                },
                {
                    version: '>= 1.0.2',
                    value: DeviceCleaningType.AVAILABLE_WHILE_CHARGING,
                    isHolderCheck: true,
                },
            ],
        },
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [{version: '>= 1.2.0', value: true}]},
        {[FEATURE_TYPES.CONNECT_CARE_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V30_HOLDER]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [{version: '>= 1.2.0', value: true, isHolderCheck: true}]},
        {[FEATURE_TYPES.EXTENDED_HAPTIC_PROFILE_VIBRATION]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER]: [
        {
            [FEATURE_TYPES.DEVICE_SELF_CLEANING]: [
                {version: '>= 0.0.0', value: DeviceCleaningType.FULL_AVAILABLE, isHolderCheck: true},
            ],
        },
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.CONNECT_CARE_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.EXTENDED_HAPTIC_PROFILE_VIBRATION]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [{version: '>= 10.7.0', value: true, isHolderCheck: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER]: [
        {
            [FEATURE_TYPES.DEVICE_SELF_CLEANING]: [
                {version: '>= 0.0.0', value: DeviceCleaningType.FULL_AVAILABLE, isHolderCheck: true},
            ],
        },
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.CONNECT_CARE_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.EXTENDED_HAPTIC_PROFILE_VIBRATION]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [{version: '>= 10.7.0', value: true, isHolderCheck: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V40C_CHARGER]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {[FEATURE_TYPES.IDLE_PING]: [{version: '> 131.5.0', value: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V40C_HOLDER_CONTROL]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.START_CHARGING_VIBRATION]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AUTOSTART_FEATURE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.STATUS_GESTURES]: [{version: '>= 120.1.0', value: true, isHolderCheck: true}]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported(true)]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V40V_CHARGER]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {[FEATURE_TYPES.IDLE_PING]: [{version: '> 131.5.0', value: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V40V_HOLDER_CONTROL]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.START_CHARGING_VIBRATION]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AUTOSTART_FEATURE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.STATUS_GESTURES]: [{version: '>= 120.1.0', value: true, isHolderCheck: true}]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported(true)]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V30M]: [
        {[FEATURE_TYPES.DEVICE_SELF_CLEANING]: [{version: '>= 1.4.1', value: DeviceCleaningType.FULL_AVAILABLE}]},
        {[FEATURE_TYPES.CONNECT_CARE_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported()},
            ],
        },
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V31M]: [
        //TODO: will be available in next fwu version of the device
        /*{
            [FEATURE_TYPES.DEVICE_SELF_CLEANING]: [{version: '>= 0.0.0', value: DeviceCleaningType.FULL_AVAILABLE}],
        },
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported()]},*/
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported()},
            ],
        },
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1_ARIANE]: [
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported()},
            ],
        },
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported()]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P4_M3_G2]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_VAPE_CLOUD_SIZE_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESPONSIVE_DRAW_PROFILE_MODE]: [allVersionSupported()]},
        {
            [FEATURE_TYPES.DEVICE_RESET]: [
                {version: '>= 80.9.0', value: true},
                {
                    version: '>= 80.7.1',
                    value: true,
                    isUsbConnectivityRequired: true,
                },
            ],
        },
        {[FEATURE_TYPES.SESSION_PROFILING]: [{version: '>= 80.8.0', value: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V40D_CONTROL]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported()},
            ],
        },
        {[FEATURE_TYPES.IDLE_PING]: [{version: '> 141.4.1', value: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41P_CHARGER]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {[FEATURE_TYPES.IDLE_PING]: [{version: '> 135.4.1', value: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41P_HOLDER_CONTROL]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.START_CHARGING_VIBRATION]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AUTOSTART_FEATURE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.STATUS_GESTURES]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AEM_FLEX_BATTERY]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.AEM_PAUSE_MODE]: [
                {version: '>= 0', toVersion: '< 124.6.1', value: true, isHolderCheck: true},
            ],
        },
        {[FEATURE_TYPES.FLEX_PUFF_FEATURE]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported(true)]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41M_CHARGER]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {[FEATURE_TYPES.IDLE_PING]: [{version: '> 135.4.1', value: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41M_HOLDER_CONTROL]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.START_CHARGING_VIBRATION]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AUTOSTART_FEATURE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.STATUS_GESTURES]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AEM_FLEX_BATTERY]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.AEM_PAUSE_MODE]: [
                {version: '>= 0', toVersion: '< 124.6.1', value: true, isHolderCheck: true},
            ],
        },
        {[FEATURE_TYPES.FLEX_PUFF_FEATURE]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported(true)]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41B_CHARGER]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {[FEATURE_TYPES.IDLE_PING]: [allVersionSupported()]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41B_HOLDER_CONTROL]: [
        {[FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.START_CHARGING_VIBRATION]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AUTOSTART_FEATURE]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.STATUS_GESTURES]: [allVersionSupported(true)]},
        {[FEATURE_TYPES.AEM_FLEX_BATTERY]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.AEM_PAUSE_MODE]: [
                {version: '>= 0', toVersion: '< 124.6.1', value: true, isHolderCheck: true},
            ],
        },
        {[FEATURE_TYPES.FLEX_PUFF_FEATURE]: [allVersionSupported(true)]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported(true)},
            ],
        },
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported(true)]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41J_CONTROL]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.STATUS_GESTURES]: [allVersionSupported()]},
        {[FEATURE_TYPES.AUTOSTART_FEATURE]: [allVersionSupported()]},
        {[FEATURE_TYPES.FLEX_PUFF_FEATURE]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported()},
            ],
        },
        {[FEATURE_TYPES.IDLE_PING]: [{version: '> 145.3.0', value: true}]},
    ],
    [DEVICE_TYPES.DEV_TYPE_P1V41A_CONTROL]: [
        {[FEATURE_TYPES.DEVICE_ALARM]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: [allVersionSupported()]},
        {[FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE]: [{version: '>= 150.5.0', value: true}]},
        {[FEATURE_TYPES.DEVICE_RESET]: [allVersionSupported()]},
        {[FEATURE_TYPES.STATUS_GESTURES]: [allVersionSupported()]},
        {[FEATURE_TYPES.AUTOSTART_FEATURE]: [allVersionSupported()]},
        {[FEATURE_TYPES.FLEX_PUFF_FEATURE]: [allVersionSupported()]},
        {[FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: [allVersionSupported()]},
        {
            [FEATURE_TYPES.SETTING_SUPPORTED]: [
                {parentNode: FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS, ...allVersionSupported()},
            ],
        },
    ],
};

const getCompareFunction = (str) => {
    //each product knows the version from which the feature is added, not the last one in which has not been added
    if (str.includes('<=')) return versionUtils.isFirstVersionLessOrEqual;
    if (str.includes('>=')) return versionUtils.isFirstVersionGreaterOrEqual;
    if (str.includes('<')) return versionUtils.isFirstVersionLess;
    if (str.includes('>')) return versionUtils.isFirstVersionGreater;
    if (str.includes('=')) return versionUtils.isFirstVersionEqual;
};

const trimVersion = (featureVersion) => featureVersion.replace(/>|<|=/g, '').trim();

const proceedCompareVersions = (version, featureVersion) => {
    const compareFunction = getCompareFunction(featureVersion);
    const clearedFeatureVersion = trimVersion(featureVersion);

    return compareFunction(version, clearedFeatureVersion);
};

const getVariableName = (variable) => Object.keys(variable)[0];

const prepareConfig = ({deviceType, version, holder, isHolderConnected}) => {
    if (!version || !deviceType) return;
    const deviceFeatures = VERSIONS_ACCORDING[deviceType];

    deviceFeatures?.forEach((featureObj) => {
        const deviceFeatureName = getVariableName(featureObj);

        for (let i = 0; i < featureObj[deviceFeatureName].length; i++) {
            const {
                version: featureVersion,
                toVersion,
                value,
                isHolderCheck,
                isUsbConnectivityRequired,
                parentNode = null,
            } = featureObj[deviceFeatureName][i];

            if (isUsbConnectivityRequired) {
                const isUsb = connectivityService.isUsb();

                if (!isUsb) return;
            }

            if (isHolderCheck) {
                const isHolderInSBLMode = iotDeviceSettingsService.isHolderInSBLMode({holder});

                if (isHolderInSBLMode || !isHolderConnected) return;
            }

            if (proceedCompareVersions(version, featureVersion)) {
                //for example if current version > 1 but toVersion < 2 then feature is disabled
                if (toVersion && !proceedCompareVersions(version, toVersion)) {
                    upperBoundFeatures.add(deviceFeatureName);
                    break;
                }

                if (parentNode) {
                    const getValue = () => (typeof value === 'function' ? value() : value);

                    deviceManagementFeaturesConfig[parentNode][deviceFeatureName] = getValue();
                } else {
                    deviceManagementFeaturesConfig[deviceFeatureName] = value;
                }
                break;
            }
        }
    });
};

const initManagementFeaturesConfig = (iotDevice) => {
    const {device, holder} = iotDevice || {};
    const {type, version, isHolderConnected} = device || {};
    const {version: holderVersion, type: holderType} = holder || {};

    prepareConfig({deviceType: type, version, holder, isHolderConnected});

    if (isHolderConnected) {
        prepareConfig({
            deviceType: holderType,
            version: holderVersion,
            holder,
            isHolderConnected,
        });
    }
};

const proceedGlobalDeviceFeature = (deviceType) => {
    const deviceFeatures = VERSIONS_ACCORDING[deviceType];

    deviceFeatures?.forEach((feature) => {
        const deviceFeatureName = getVariableName(feature);

        switch (deviceFeatureName) {
            case FEATURE_TYPES.AUTOSTART_FEATURE:
                deviceGlobalFeaturesConfig.is_autostart = true;
                break;
            case FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE:
                deviceGlobalFeaturesConfig.is_led_intensity = true;
                break;
            case FEATURE_TYPES.EXTENDED_DEVICE_LED_INTENSITY_MODE:
                deviceGlobalFeaturesConfig.is_extended_led_intensity = true;
                break;
            case FEATURE_TYPES.START_CHARGING_VIBRATION:
            case FEATURE_TYPES.SETTING_SUPPORTED:
            case FEATURE_TYPES.DEVICE_HOLDER_CHARGED_VIBRATION:
            case FEATURE_TYPES.EXTENDED_HAPTIC_PROFILE_VIBRATION:
            case FEATURE_TYPES.COMMON_VIBRATION_SWITCHER:
                deviceGlobalFeaturesConfig.is_vibration = true;
                break;
            case FEATURE_TYPES.DEVICE_SELF_CLEANING:
                deviceGlobalFeaturesConfig.is_self_cleaning = true;
                break;
            case FEATURE_TYPES.DEVICE_ALARM:
                deviceGlobalFeaturesConfig.is_device_alarm = true;
                break;
            case FEATURE_TYPES.STATUS_GESTURES:
                deviceGlobalFeaturesConfig.is_status_gestures = true;
                break;
            case FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE:
                deviceGlobalFeaturesConfig.is_parent_protection = true;
                break;
            case FEATURE_TYPES.SESSION_PROFILING:
                deviceGlobalFeaturesConfig.is_session_profiling = true;
                break;
            case FEATURE_TYPES.ADVANCED_DIAGNOSTIC:
                deviceGlobalFeaturesConfig.is_advanced_diagnostic = true;
                break;
            case FEATURE_TYPES.DEVICE_VAPE_CLOUD_SIZE_MODE:
                deviceGlobalFeaturesConfig.is_vape_cloud_size = true;
                break;
            case FEATURE_TYPES.DEVICE_RESPONSIVE_DRAW_PROFILE_MODE:
                deviceGlobalFeaturesConfig.is_responsive_draw = true;
                break;
            case FEATURE_TYPES.DEVICE_RESET:
                deviceGlobalFeaturesConfig.is_reset = true;
                break;
            case FEATURE_TYPES.CONNECT_CARE_MODE:
                deviceGlobalFeaturesConfig.is_connect_care = true;
                break;
            case FEATURE_TYPES.AEM_FLEX_BATTERY:
                deviceGlobalFeaturesConfig.is_aem_flex_battery = true;
                break;
            case FEATURE_TYPES.AEM_PAUSE_MODE:
                deviceGlobalFeaturesConfig.is_aem_pause_mode = true;
                break;
            case FEATURE_TYPES.FLEX_PUFF_FEATURE:
                deviceGlobalFeaturesConfig.is_flex_puff = true;
                break;
            case FEATURE_TYPES.IDLE_PING:
                deviceGlobalFeaturesConfig.is_idle_ping = true;
                break;
            default:
                return;
        }
    });
};

const prepareGlobalConfigByDevice = (iotDevice) => {
    const {device, holder} = iotDevice || {};
    const chargerType = device?.type;
    const holderType = holder?.type;
    const getIsHolderCheck = (deviceType) => {
        const holderCheckDeviceArray = [
            DEVICE_TYPES.DEV_TYPE_V24_CHARGER,
            DEVICE_TYPES.DEV_TYPE_V24P_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V30_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V40V_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V40C_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V41P_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V41M_CHARGER,
            DEVICE_TYPES.DEV_TYPE_P1V41B_CHARGER,
        ];

        return holderCheckDeviceArray.some((device) => device === deviceType);
    };

    resetGlobalConfig();

    deviceGlobalFeaturesConfig.is_holder_check = getIsHolderCheck(chargerType);

    proceedGlobalDeviceFeature(chargerType);

    const getHolderType = (deviceType) => {
        switch (deviceType) {
            case DEVICE_TYPES.DEV_TYPE_V24P_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_V24P_HOLDER;
            case DEVICE_TYPES.DEV_TYPE_P1V30_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V30_HOLDER;
            case DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER;
            case DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER;
            case DEVICE_TYPES.DEV_TYPE_P1V40C_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V40C_HOLDER_CONTROL;
            case DEVICE_TYPES.DEV_TYPE_P1V40V_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V40V_HOLDER_CONTROL;
            case DEVICE_TYPES.DEV_TYPE_P1V41P_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V41P_HOLDER_CONTROL;
            case DEVICE_TYPES.DEV_TYPE_P1V41M_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V41M_HOLDER_CONTROL;
            case DEVICE_TYPES.DEV_TYPE_P1V41B_CHARGER:
                return DEVICE_TYPES.DEV_TYPE_P1V41B_HOLDER_CONTROL;
            default:
                break;
        }
    };

    const preparedHolderType = holderType ? holderType : getHolderType(chargerType);

    if (preparedHolderType) {
        proceedGlobalDeviceFeature(preparedHolderType);
    }
};

const getManagementFeaturesConfig = () => deviceManagementFeaturesConfig;

const getGlobalFeaturesConfig = (iotDevice) => {
    prepareGlobalConfigByDevice(iotDevice);

    return deviceGlobalFeaturesConfig;
};

const getDeviceSupportedFeatures = ({deviceType, version, updateVersion}) => {
    const deviceFeatures = VERSIONS_ACCORDING[deviceType];

    if (!deviceFeatures) return [];

    const parseFeatureName = (featureName) => {
        switch (featureName) {
            case FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE:
            case FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE:
                return appConfig.getIsLedEnabled() && getFeatureTypes.LED;
            case FEATURE_TYPES.EXTENDED_DEVICE_LED_INTENSITY_MODE:
            case FEATURE_TYPES.EXTENDED_HOLDER_LED_INTENSITY_MODE:
                return appConfig.getIsLedEnabled() && getFeatureTypes.EXTENDED_LED;
            case FEATURE_TYPES.DEVICE_VAPE_CLOUD_SIZE_MODE:
                return appConfig.getIsVapeCloudSizeEnabled() && getFeatureTypes.VAPE_CLOUD_SIZE;
            case FEATURE_TYPES.DEVICE_RESPONSIVE_DRAW_PROFILE_MODE:
                return appConfig.getIsResponsiveDrawProfileEnabled() && getFeatureTypes.RESPONSIVE_DRAW;
            case FEATURE_TYPES.COUNTDOWN_MODE:
                return getFeatureTypes.COUNTDOWN;
            case FEATURE_TYPES.DEVICE_ALARM:
                return appConfig.getIsDeviceAlarmEnabled() && getFeatureTypes.LOCATE_MY_DEVICE;
            case FEATURE_TYPES.ADVANCED_DIAGNOSTIC:
                return (
                    appConfig.getIsAdvancedDeviceDiagnosticEnabled(deviceType) && getFeatureTypes.ADVANCED_DIAGNOSTIC
                );
            case FEATURE_TYPES.STATUS_GESTURES:
                return appConfig.getIsGestureEnabled() && getFeatureTypes.GESTURE;
            case FEATURE_TYPES.AUTOSTART_FEATURE:
                return appConfig.getIsAutoStartEnabled() && getFeatureTypes.AUTOSTART;
            case FEATURE_TYPES.CONNECT_CARE_MODE:
                return appConfig.getIsConnectCareEnabled(deviceType) && getFeatureTypes.DIAGNOSTIC;
            case FEATURE_TYPES.DEVICE_SELF_CLEANING:
                return appConfig.getIsSelfCleaningEnabled() && getFeatureTypes.CLEANING;
            case FEATURE_TYPES.EXTENDED_HAPTIC_PROFILE_VIBRATION:
            case FEATURE_TYPES.DEVICE_HOLDER_CHARGED_VIBRATION:
            case FEATURE_TYPES.COMMON_VIBRATION_SWITCHER:
            case FEATURE_TYPES.START_CHARGING_VIBRATION:
            case FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS:
                return appConfig.getIsVibrationEnabled() && getFeatureTypes.VIBRATION;
            case FEATURE_TYPES.DEVICE_RESET:
                return appConfig.getIsDeviceResetFeatureEnabled() && getFeatureTypes.DEVICE_RESET;
            case FEATURE_TYPES.SESSION_PROFILING:
                return appConfig.getIsSessionProfilingEnabled() && getFeatureTypes.VAPE_SESSION;
            case FEATURE_TYPES.DEVICE_PARENT_PROTECTION_MODE:
                return appConfig.getIsParentProtectionEnabled() && getFeatureTypes.DEVICE_PARENT_PROTECTION;
            case FEATURE_TYPES.AEM_FLEX_BATTERY:
                return appConfig.getIsAemFlexBatteryEnabled() && getFeatureTypes.AEM_FLEX_BATTERY;
            case FEATURE_TYPES.AEM_PAUSE_MODE:
                return appConfig.getIsAemFlexBatteryEnabled() && getFeatureTypes.AEM_PAUSE_MODE;
            case FEATURE_TYPES.FLEX_PUFF_FEATURE:
                return appConfig.getIsFlexPuffEnabled() && getFeatureTypes.FLEX_PUFF;
            default:
                break;
        }
    };

    let resultFeaturesArray = [];
    const isUsb = connectivityService.isUsb();

    deviceFeatures?.forEach((featureObj) => {
        const deviceFeatureName = getVariableName(featureObj);
        const featuresOptions = featureObj[deviceFeatureName];
        const featuresOptionsLength = featuresOptions?.length;

        if (!featuresOptionsLength) return;

        let isFeatureCanBeLoaded = true;

        for (let i = 0; i < featuresOptionsLength; i++) {
            if (!isFeatureCanBeLoaded) break;

            const {version: featureVersion, isUsbConnectivityRequired} = featuresOptions[i];
            const clearedFeatureVersion = trimVersion(featureVersion);

            if (isUsbConnectivityRequired && !isUsb) continue;

            isFeatureCanBeLoaded =
                versionUtils.isFirstVersionGreater(clearedFeatureVersion, version) &&
                versionUtils.isFirstVersionGreaterOrEqual(updateVersion, clearedFeatureVersion);
        }

        if (isFeatureCanBeLoaded) {
            const {parentNode = null} = featuresOptions[0];
            const featureName = parentNode ? parentNode : deviceFeatureName;
            const parsedFeatureName = parseFeatureName(featureName);
            const isFeatureAlreadyExist = resultFeaturesArray.some((feature) => feature === parsedFeatureName);

            if (!isFeatureAlreadyExist && parsedFeatureName) {
                resultFeaturesArray.push(parsedFeatureName);
            }
        }
    });

    const isDoubleDiagnosticsPresent =
        resultFeaturesArray.some((feature) => feature === getFeatureTypes.DIAGNOSTIC) &&
        resultFeaturesArray.some((feature) => feature === getFeatureTypes.ADVANCED_DIAGNOSTIC);

    if (isDoubleDiagnosticsPresent) {
        //Need to exclude double diagnostics on get tag
        resultFeaturesArray = resultFeaturesArray.filter((feature) => feature !== getFeatureTypes.DIAGNOSTIC);
    }

    return resultFeaturesArray;
};

const featuresTypeMapping = {
    [FEATURE_TYPES.AUTOSTART_FEATURE]: deviceSettingTypes.AUTO_START_MODE,
    [FEATURE_TYPES.CONNECT_CARE_MODE]: deviceSettingTypes.CONNECT_CARE,
    [FEATURE_TYPES.DEVICE_RESET]: deviceSettingTypes.RESTART,
    [FEATURE_TYPES.DEVICE_ALARM]: deviceSettingTypes.LOCATE_MY_DEVICE,
    [FEATURE_TYPES.DEVICE_HOLDER_CHARGED_VIBRATION]: deviceSettingTypes.VIBRATION_MODE,
    [FEATURE_TYPES.DEVICE_LED_INTENSITY_MODE]: deviceSettingTypes.LED_SETTING,
    [FEATURE_TYPES.EXTENDED_DEVICE_LED_INTENSITY_MODE]: deviceSettingTypes.EXTENDED_LED_SETTING,
    [FEATURE_TYPES.DEVICE_RESPONSIVE_DRAW_PROFILE_MODE]: deviceSettingTypes.RESPONSIVE_DRAW,
    [FEATURE_TYPES.DEVICE_SELF_CLEANING]: deviceSettingTypes.CLEANING_MODE,
    [FEATURE_TYPES.DEVICE_VAPE_CLOUD_SIZE_MODE]: deviceSettingTypes.VAPE_CLOUD_SIZE,
    [FEATURE_TYPES.EXTENDED_HAPTIC_PROFILE_VIBRATION]: deviceSettingTypes.VIBRATION_MODE,
    [FEATURE_TYPES.HOLDER_LED_INTENSITY_MODE]: deviceSettingTypes.LED_SETTING,
    [FEATURE_TYPES.EXTENDED_HOLDER_LED_INTENSITY_MODE]: deviceSettingTypes.EXTENDED_LED_SETTING,
    [FEATURE_TYPES.SETTING_SUPPORTED]: deviceSettingTypes.VIBRATION_MODE,
    [FEATURE_TYPES.COMMON_VIBRATION_SWITCHER]: deviceSettingTypes.VIBRATION_MODE,
    [FEATURE_TYPES.START_CHARGING_VIBRATION]: deviceSettingTypes.VIBRATION_MODE,
    [FEATURE_TYPES.STATUS_GESTURES]: deviceSettingTypes.GESTURE_MODE,
    [FEATURE_TYPES.PROGRAMMABLE_VIBRATIONS]: deviceSettingTypes.VIBRATION_MODE,
    [FEATURE_TYPES.SESSION_PROFILING]: deviceSettingTypes.VAPE_SESSION,
    [FEATURE_TYPES.ADVANCED_DIAGNOSTIC]: deviceSettingTypes.DEVICE_ADVANCED_DIAGNOSTIC,
    [FEATURE_TYPES.AEM_FLEX_BATTERY]: deviceSettingTypes.AEM_FLEX_BATTERY,
    [FEATURE_TYPES.AEM_PAUSE_MODE]: deviceSettingTypes.AEM_PAUSE_MODE,
    [FEATURE_TYPES.FLEX_PUFF_FEATURE]: deviceSettingTypes.FLEX_PUFF,
};

const isHolderFeatureRelated = (type, holderType, settingType) => {
    const isDeviceRelated = isFeatureExistsForDevice(type, settingType);

    if (isDeviceRelated !== undefined) return !isDeviceRelated;

    return isFeatureExistsForDevice(holderType, settingType);
};

const isFeatureExistsForDevice = (type, settingType) => {
    let isExists;
    const deviceFeatures = VERSIONS_ACCORDING[type];

    deviceFeatures?.forEach((featureObj) => {
        if (isExists) return;

        const deviceFeatureName = getVariableName(featureObj);

        if (featuresTypeMapping[deviceFeatureName] === settingType) {
            isExists = true;
        }
    });

    return isExists;
};

const isFeatureUpperBound = (featureName) => upperBoundFeatures.has(featureName);

export {FEATURE_TYPES};

export default {
    getDeviceSupportedFeatures,
    getManagementFeaturesConfig,
    getGlobalFeaturesConfig,
    isFeatureUpperBound,
    isHolderFeatureRelated,
    resetManagementFeaturesConfig,
};
